import { useState, useCallback, useEffect, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { usePersistedWalletAddress } from 'dibs-crypto-utils/exports/usePersistedWalletAddress';

import { NftPdpContractContext } from '../../NftPdpContractContext/NftPdpContractContext';
import { type useLatestUserInfo_viewer$key } from './__generated__/useLatestUserInfo_viewer.graphql';
import { type useLatestUserInfo_nftItem$key } from './__generated__/useLatestUserInfo_nftItem.graphql';

export function useLatestUserInfo({
    viewer: viewerRef,
    nftItem: nftItemRef,
    currentAuctionWinnerAddress,
}: {
    viewer: useLatestUserInfo_viewer$key | null | undefined;
    nftItem: useLatestUserInfo_nftItem$key;
    currentAuctionWinnerAddress?: string | null;
}): {
    hasMismatchedAddress: boolean;
    isOwner: boolean;
    isHighestBidder: boolean;
    isSeller: boolean;
    userHasBidderRole: boolean;
    userId?: string | null;
    setUserHasBidderRole: (hasBidderRole: boolean) => void;
    persistedAddress: string;
    fetchHasBidderRole: (persistedAddress: string) => Promise<void>;
    fetchingBidderRole: boolean;
} {
    const {
        account: connectedAddress,
        chainId,
        awaitingAddressAndBidderRole,
    } = useWeb3Connection();

    const nftItem = useFragment(
        graphql`
            fragment useLatestUserInfo_nftItem on NftItem {
                mostRecentAuction {
                    sellerAddress {
                        address
                    }
                }
                balanceForUser(userId: $userId) @include(if: $fetchUser) {
                    balance
                }
            }
        `,
        nftItemRef
    );

    const viewer = useFragment(
        graphql`
            fragment useLatestUserInfo_viewer on Viewer {
                ...usePersistedWalletAddress_viewer @arguments(fetchUser: false, userId: "")
            }
        `,
        viewerRef
    );

    const { userId, persistedAddress } = usePersistedWalletAddress({ viewer });

    const { mostRecentAuction, balanceForUser } = nftItem || {};
    const { sellerAddress } = mostRecentAuction || {};

    const { auctionContractV1 } = useContext(NftPdpContractContext);

    const [userHasBidderRole, setUserHasBidderRole] = useState<boolean>(false);
    const [fetchingBidderRole, setFetchingBidderRole] = useState(false);
    const fetchHasBidderRole = useCallback(
        async (persistedAddressArg: string) => {
            if (auctionContractV1) {
                try {
                    setFetchingBidderRole(true);
                    // eslint-disable-next-line new-cap
                    const bidderRole = await auctionContractV1.BIDDER_ROLE();
                    if (bidderRole && persistedAddressArg) {
                        const hasBidderRole = await auctionContractV1.hasRole(
                            bidderRole,
                            persistedAddressArg
                        );
                        setUserHasBidderRole(!!hasBidderRole);
                    }
                    setFetchingBidderRole(false);
                } catch (e) {
                    setUserHasBidderRole(false);
                    setFetchingBidderRole(false);
                }
            }
        },
        [auctionContractV1]
    );

    const [hasMismatchedAddress, setHasMismatchedAddress] = useState(false);

    useEffect(() => {
        (async () => {
            if (persistedAddress) {
                await fetchHasBidderRole(persistedAddress);
            }
        })();
    }, [fetchHasBidderRole, persistedAddress, awaitingAddressAndBidderRole]);

    useEffect(() => {
        if (chainId && connectedAddress) {
            setHasMismatchedAddress(connectedAddress.toLowerCase() !== persistedAddress);
        }
    }, [chainId, connectedAddress, persistedAddress]);

    const isHighestBidder = Boolean(
        persistedAddress &&
            currentAuctionWinnerAddress &&
            persistedAddress === currentAuctionWinnerAddress
    );
    const isOwner = Boolean(balanceForUser?.balance && balanceForUser?.balance > 0);
    const isSeller = Boolean(
        persistedAddress &&
            sellerAddress &&
            persistedAddress === sellerAddress?.address?.toLowerCase()
    );

    return {
        hasMismatchedAddress,
        isHighestBidder,
        isOwner,
        isSeller,
        userHasBidderRole,
        userId,
        persistedAddress,
        setUserHasBidderRole,
        fetchHasBidderRole,
        fetchingBidderRole,
    };
}
